// How a measurable reads to a human, as opposed to how it's stored.
//
// Storage is canonical (lengths in inches) because that's what the API, the
// search filters and the Power Score compute on. Display is not: nobody quotes
// a hand as 9.375". This is the single place that translation happens for web,
// so a coach sees the same string on a profile, a report and a comparison table.
//
// Port of ios/ProZpects/Core/MeasurableDisplay.swift. Same snapping, same
// fraction reduction, same carry. The one deliberate difference is the glyph:
// web quotes inches with an ASCII double quote (9 3/8"), mobile uses a prime
// (9 3/8″). Everything else must stay identical or the three-way card-text
// parity breaks.

export const EM_DASH = '—';

// Measurables a coach reads in feet and inches, and the one read in eighths.
// Keyed by measurable key, never by sport: any sport whose catalog carries
// `wingspan` reads it the same way. Mirrors FMT_BY_MEASURABLE_KEY in
// backend/app/services/report_columns.py.
// Height and wingspan are measured to the nearest 1/8 inch at a camp station —
// height barefoot against a standardized device, wingspan by hand with a tape,
// fingertip to fingertip with arms horizontal. Rounding those to whole inches
// at display time threw the precision away: 76.375 and 76.625 both rendered
// 6'4", so a station that captured eighths showed a coach nothing extra.
export const FEET_EIGHTHS_KEYS = ['height_inches', 'wingspan'];

// Broad jump stays whole-inch. It shares the feet-inches shape but is
// conventionally recorded and compared in whole inches, and moving it would
// silently change a measurement nobody asked to change.
export const FEET_INCHES_KEYS = ['broad_jump'];

export const EIGHTH_INCH_KEYS = ['hand_size'];

/**
 * 74 → `6'2"`, 72.75 → `6'1"`, 124 → `10'4"`.
 *
 * Rounds to whole inches BEFORE dividing. Flooring first and rounding the
 * remainder instead yields an impossible `5'12"`, and a raw float modulo on a
 * stored 73.3 yields `6'1.2999999999999972"`.
 *
 * @param {number|string|null|undefined} value inches as stored
 * @returns {string} the display string, or an em dash when there's no value
 */
export function formatFeetInches(value) {
  const inches = typeof value === 'number' ? value : parseFloat(value);
  if (!Number.isFinite(inches) || inches <= 0) return EM_DASH;

  const total = Math.round(inches);
  return `${Math.floor(total / 12)}'${total % 12}"`;
}

// 2/8, 4/8 and 6/8 reduce; scouting sheets write 1/2, not 4/8. The odd eighths
// (1, 3, 5, 7) are already in lowest terms and fall through to [n, 8].
const REDUCED_EIGHTHS = {
  2: [1, 4],
  4: [1, 2],
  6: [3, 4],
};

/**
 * 9.375 → `9 3/8"`, 9.5 → `9 1/2"`, 10 → `10"`, 9.97 → `10"`.
 *
 * Snaps to the nearest eighth BEFORE splitting into whole + remainder, so a
 * LiDAR or caliper reading of 9.37 doesn't surface as an un-scoutable decimal,
 * and 9.97 carries into the whole inch instead of rendering `9 8/8"`.
 *
 * @param {number|string|null|undefined} value inches as stored
 * @returns {string} the display string, or an em dash when there's no value
 */
export function formatEighthInches(value) {
  const inches = typeof value === 'number' ? value : parseFloat(value);
  if (!Number.isFinite(inches) || inches <= 0) return EM_DASH;

  const eighths = Math.round(inches * 8);
  const whole = Math.floor(eighths / 8);
  const remainder = eighths % 8;

  if (remainder === 0) return `${whole}"`;

  const [numerator, denominator] = REDUCED_EIGHTHS[remainder] || [remainder, 8];
  return `${whole} ${numerator}/${denominator}"`;
}

/**
 * 73.625 → `6'1 5/8"`, 76 → `6'4"`, 76.97 → `6'5"`.
 *
 * Snaps to the nearest EIGHTH before splitting, then carries a full 8/8 into
 * the inch and a full 12" into the foot. Doing it in that order is what stops
 * `5'12"` and `6'4 8/8"` — both of which a naive floor-then-remainder produces.
 *
 * @param {number|string|null|undefined} value inches as stored
 * @returns {string} the display string, or an em dash when there's no value
 */
export function formatFeetEighths(value) {
  const inches = typeof value === 'number' ? value : parseFloat(value);
  if (!Number.isFinite(inches) || inches <= 0) return EM_DASH;

  const totalEighths = Math.round(inches * 8);
  const wholeInches = Math.floor(totalEighths / 8);
  const remainder = totalEighths % 8;

  const feet = Math.floor(wholeInches / 12);
  const inchPart = wholeInches % 12;

  if (remainder === 0) return `${feet}'${inchPart}"`;

  const [numerator, denominator] = REDUCED_EIGHTHS[remainder] || [remainder, 8];
  return `${feet}'${inchPart} ${numerator}/${denominator}"`;
}

/**
 * Format a stored value by its MEASURABLE KEY, the way the report PDF does.
 *
 * Mirrors `fmt_for_key` in backend/app/services/report_columns.py so the public
 * report's HTML twin and its server-rendered PDF can't disagree. Keys the
 * catalog doesn't call out fall through unchanged — a 40 time or a bench press
 * is already human-readable as stored.
 *
 * @param {string} key measurable key, e.g. 'wingspan'
 * @param {number|string|null|undefined} value inches as stored
 * @returns {string} display string; em dash when there's no value
 */
export function formatByKey(key, value) {
  if (FEET_EIGHTHS_KEYS.includes(key)) return formatFeetEighths(value);
  if (FEET_INCHES_KEYS.includes(key)) return formatFeetInches(value);
  if (EIGHTH_INCH_KEYS.includes(key)) return formatEighthInches(value);
  return value == null || value === '' ? EM_DASH : String(value);
}

/**
 * Does this key carry its own display format, or is it readable as stored?
 *
 * The single source of truth for that question. Callers that need to choose
 * between `formatByKey` and a plain unit suffix must ask HERE rather than
 * re-listing the key arrays: publicProfile.js used to test
 * `FEET_INCHES_KEYS || EIGHTH_INCH_KEYS` inline, and when height and wingspan
 * moved into the new FEET_EIGHTHS_KEYS (15bb3104) that copy was not updated,
 * so public profiles rendered a height as `73"` instead of `6'1"`. Adding a
 * fourth list must not be able to reintroduce that.
 *
 * @param {string} key measurable key
 * @returns {boolean}
 */
export function hasKeyFormat(key) {
  return FEET_EIGHTHS_KEYS.includes(key)
    || FEET_INCHES_KEYS.includes(key)
    || EIGHTH_INCH_KEYS.includes(key);
}

export default formatEighthInches;
